// StrategyConfig.js
import React, { useState } from 'react';
import axiosInstance from '../../axiosConfig';
import GlassPaper from '../GlassPaper';
import { useAdminStrategies } from '../../hooks/useAdminStrategies';
import { isBindingsStrategy, strategyModelLabel } from '../../utils/strategyAccountModel';
import './StrategyConfig.css';

const StrategyConfig = () => {
    const { strategies, loading: strategiesLoading } = useAdminStrategies();
    const [selectedStrategy, setSelectedStrategy] = useState('');
    const [strategyDesc, setStrategyDesc] = useState('');
    const [parameters, setParameters] = useState([]);
    const [parameterValues, setParameterValues] = useState({});
    const [originalValues, setOriginalValues] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [responseColor, setResponseColor] = useState('');
    const [hasChanges, setHasChanges] = useState(false);

    const selectedMeta = strategies.find((s) => s.strategy_name === selectedStrategy);

    const handleStrategyChange = async (e) => {
        const selected = e.target.value;
        setSelectedStrategy(selected);
        const strategy = strategies.find(strategy => strategy.strategy_name === selected);
        setStrategyDesc(strategy ? strategy.strategy_desc : '');
        setResponseMessage('');
        setHasChanges(false);

        if (selected) {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/api/strategy-config/${selected}`);
                const params = response.data;

                // Initialize parameter values
                const values = {};
                params.forEach(param => {
                    values[param.param_name] = param.param_value;
                });

                setParameters(params);
                setParameterValues(values);
                setOriginalValues({ ...values });
            } catch (error) {
                console.error('Error fetching strategy config:', error);
                setResponseMessage('Error loading strategy parameters');
                setResponseColor('red');
            } finally {
                setLoading(false);
            }
        } else {
            setParameters([]);
            setParameterValues({});
            setOriginalValues({});
        }
    };

    const handleParameterChange = (paramName, value) => {
        const updatedValues = {
            ...parameterValues,
            [paramName]: value
        };
        setParameterValues(updatedValues);

        // Check if there are any changes
        const changed = Object.keys(updatedValues).some(
            key => updatedValues[key] !== originalValues[key]
        );
        setHasChanges(changed);
    };

    const handleSave = async () => {
        if (!hasChanges) {
            setResponseMessage('No changes to save');
            setResponseColor('orange');
            return;
        }

        setSaving(true);
        try {
            // Collect changed parameters
            const updates = [];
            Object.keys(parameterValues).forEach(paramName => {
                if (parameterValues[paramName] !== originalValues[paramName]) {
                    const param = parameters.find(p => p.param_name === paramName);
                    updates.push({
                        param_name: paramName,
                        param_value: parameterValues[paramName],
                        param_type: param.param_type
                    });
                }
            });

            await axiosInstance.post(`/api/strategy-config/${selectedStrategy}`, {
                updates: updates
            });

            setResponseMessage(`Successfully updated ${updates.length} parameter(s)`);
            setResponseColor('green');
            setOriginalValues({ ...parameterValues });
            setHasChanges(false);
        } catch (error) {
            console.error('Error saving strategy config:', error);
            setResponseMessage('Error saving parameters');
            setResponseColor('red');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        setParameterValues({ ...originalValues });
        setHasChanges(false);
        setResponseMessage('');
    };

    // Group parameters by category
    const groupedParameters = parameters.reduce((groups, param) => {
        const category = param.category || 'other';
        if (!groups[category]) {
            groups[category] = [];
        }
        groups[category].push(param);
        return groups;
    }, {});

    // Sort categories for consistent display
    const categoryOrder = ['execution', 'timing', 'sizing', 'risk', 'technical', 'instrument', 'reentry', 'options', 'data', 'other'];
    const sortedCategories = Object.keys(groupedParameters).sort((a, b) => {
        const indexA = categoryOrder.indexOf(a);
        const indexB = categoryOrder.indexOf(b);
        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    const renderInput = (param) => {
        const value = parameterValues[param.param_name] || '';

        switch (param.param_type) {
            case 'bool':
                return (
                    <select
                        className="form-control"
                        value={value.toString()}
                        onChange={(e) => handleParameterChange(param.param_name, e.target.value === 'true')}
                    >
                        <option value="true">true</option>
                        <option value="false">false</option>
                    </select>
                );
            case 'int':
            case 'float':
                return (
                    <input
                        type="number"
                        className="form-control"
                        value={value}
                        step={param.param_type === 'float' ? '0.01' : '1'}
                        onChange={(e) => handleParameterChange(param.param_name, param.param_type === 'int' ? parseInt(e.target.value) || 0 : parseFloat(e.target.value) || 0)}
                    />
                );
            case 'list':
                return (
                    <textarea
                        className="form-control"
                        value={typeof value === 'string' ? value : JSON.stringify(value)}
                        rows={2}
                        onChange={(e) => {
                            try {
                                const parsed = JSON.parse(e.target.value);
                                handleParameterChange(param.param_name, parsed);
                            } catch {
                                // Allow intermediate invalid JSON during editing
                                handleParameterChange(param.param_name, e.target.value);
                            }
                        }}
                    />
                );
            default:
                return (
                    <input
                        type="text"
                        className="form-control"
                        value={value}
                        onChange={(e) => handleParameterChange(param.param_name, e.target.value)}
                    />
                );
        }
    };

    return (
        <GlassPaper className="config-shell" elevation={0} square={false}>
            <h2 className="dashboard-title">Config</h2>

            <div className="form-group mt-3">
                <label htmlFor="strategySelect">Select Strategy</label>
                <select
                    id="strategySelect"
                    className="form-control"
                    value={selectedStrategy}
                    onChange={handleStrategyChange}
                    disabled={strategiesLoading}
                >
                    <option value="">Select a strategy</option>
                    {strategies.map((strategy, index) => (
                        <option key={index} value={strategy.strategy_name}>
                            {strategy.strategy_name}
                        </option>
                    ))}
                </select>
                {strategyDesc && <p className="description-text">{strategyDesc}</p>}
                {selectedMeta && (
                    <p className="description-text">
                        {strategyModelLabel(selectedMeta)}
                        {isBindingsStrategy(selectedMeta)
                            ? ' — assign accounts under Strategy accounts tab.'
                            : ' — assign lots under Client lots tab.'}
                    </p>
                )}
            </div>

            {loading && (
                <div className="loading-message">Loading parameters...</div>
            )}

            {!loading && parameters.length > 0 && (
                <div className="parameters-container">
                    {sortedCategories.map(category => (
                        <div key={category} className="category-section">
                            <h3 className="category-title">{category.toUpperCase()}</h3>
                            <div className="parameters-grid">
                                {groupedParameters[category]
                                    .sort((a, b) => (a.display_order || 999) - (b.display_order || 999))
                                    .map(param => (
                                        <div key={param.param_name} className="parameter-item">
                                            <label className="parameter-label">
                                                {param.param_name}
                                                <span className="parameter-type">({param.param_type})</span>
                                            </label>
                                            {param.description && (
                                                <p className="parameter-description">{param.description}</p>
                                            )}
                                            {renderInput(param)}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!loading && selectedStrategy && parameters.length === 0 && (
                <div className="no-params-message">
                    No editable parameters found for this strategy.
                </div>
            )}

            {selectedStrategy && parameters.length > 0 && (
                <div className="action-buttons">
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={handleReset}
                        disabled={!hasChanges || saving}
                    >
                        Reset
                    </button>
                    <button
                        className="btn btn-sm btn-primary"
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            )}

            {responseMessage && (
                <div className="response-message" style={{ color: responseColor }}>
                    {responseMessage}
                    <button
                        className="btn btn-secondary btn-sm mt-2"
                        onClick={() => setResponseMessage('')}
                    >
                        Ok
                    </button>
                </div>
            )}
        </GlassPaper>
    );
};

export default StrategyConfig;
